/**
 * useTeamLeaderAssignment Hook
 * V137.4: 팀장(TL) 역할 배정/해제 Hook
 *
 * setTeamLeaderRoleForUser 백엔드 함수를 호출하여
 * 직원에게 팀장 역할(role_tl)을 배정하거나 해제합니다.
 *
 * **사용 예시:**
 * ```tsx
 * function OrgMembersPage() {
 *   const { assignTeamLeader, removeTeamLeader, isLoading, error } = useTeamLeaderAssignment();
 *
 *   const handleAssign = async (employeeId: string) => {
 *     const result = await assignTeamLeader(employeeId);
 *     if (result.success) {
 *       // 성공 처리
 *     }
 *   };
 *
 *   const handleRemove = async (employeeId: string) => {
 *     const result = await removeTeamLeader(employeeId);
 *     if (result.success) {
 *       // 성공 처리
 *     }
 *   };
 * }
 * ```
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

const ROLE_TL = 'role_tl';

/**
 * 백엔드 응답 타입
 */
interface SetTeamLeaderRoleResponse {
  success: boolean;
  message: string;
  roleIds?: string[];
}

/**
 * useTeamLeaderAssignment 반환 타입
 */
export interface UseTeamLeaderAssignmentResult {
  /** 팀장 역할 배정 */
  assignTeamLeader: (employeeId: string) => Promise<SetTeamLeaderRoleResponse>;

  /** 팀장 역할 해제 */
  removeTeamLeader: (employeeId: string) => Promise<SetTeamLeaderRoleResponse>;

  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;
}

/**
 * 직원이 팀장 역할을 가지고 있는지 확인
 */
export function hasTeamLeaderRole(roleIds: string[] | undefined): boolean {
  return roleIds?.includes(ROLE_TL) ?? false;
}

/**
 * useTeamLeaderAssignment Hook
 */
export function useTeamLeaderAssignment(): UseTeamLeaderAssignmentResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const setTeamLeaderRole = useCallback(
    async (employeeId: string, assign: boolean): Promise<SetTeamLeaderRoleResponse> => {
      // Firebase가 초기화되지 않은 경우 (DEMO 모드)
      if (!functions) {
        return {
          success: false,
          message: 'Firebase가 초기화되지 않았습니다.',
        };
      }

      setIsLoading(true);
      setError(null);

      try {
        const fn = httpsCallable<
          { employeeId: string; assign: boolean },
          SetTeamLeaderRoleResponse
        >(functions, 'setTeamLeaderRoleForUser');

        const result = await fn({ employeeId, assign });

        return result.data;
      } catch (err: any) {
        console.error('[useTeamLeaderAssignment] 오류:', err);
        setError(err);
        return {
          success: false,
          message: err.message || '알 수 없는 오류가 발생했습니다.',
        };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const assignTeamLeader = useCallback(
    (employeeId: string) => setTeamLeaderRole(employeeId, true),
    [setTeamLeaderRole]
  );

  const removeTeamLeader = useCallback(
    (employeeId: string) => setTeamLeaderRole(employeeId, false),
    [setTeamLeaderRole]
  );

  return {
    assignTeamLeader,
    removeTeamLeader,
    isLoading,
    error,
  };
}
